/**
 * Estimate Settings — Settings → Estimates panel.
 *
 * Admin-only panel that drives the company-wide defaults for every estimate
 * built in the field: validity period, deposit rules, labour-role catalogue,
 * vehicle billing defaults, prefix + T&C boilerplate.
 *
 * The same payload is consumed by EstimateBuilder.jsx so the builder always
 * reflects the latest admin config without a code change.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Plus, Trash2, Loader2, FileText } from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

export default function EstimateSettings({ token }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [newRole, setNewRole] = useState({ name: '', rate_per_hour: '' });

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/estimate-settings`, { headers: { Authorization: `Bearer ${token}` } });
      setSettings(res.data);
    } catch (e) {
      toast({ title: 'Failed to load estimate settings', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); /* eslint-disable-line */ }, []);

  const save = async (overrides = {}) => {
    setSaving(true);
    try {
      const payload = { ...settings, ...overrides };
      const res = await axios.put(`${API}/estimate-settings`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setSettings(res.data);
      toast({ title: 'Settings saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const addRole = async () => {
    const name = (newRole.name || '').trim();
    if (!name) return;
    const rate = parseFloat(newRole.rate_per_hour) || 0;
    const updated = [...(settings.labour_roles || []), { name, rate_per_hour: rate }];
    setNewRole({ name: '', rate_per_hour: '' });
    await save({ labour_roles: updated });
  };

  const removeRole = async (idx) => {
    const updated = (settings.labour_roles || []).filter((_, i) => i !== idx);
    await save({ labour_roles: updated });
  };

  const updateRole = (idx, patch) => {
    const updated = (settings.labour_roles || []).map((r, i) => i === idx ? { ...r, ...patch } : r);
    setSettings({ ...settings, labour_roles: updated });
  };

  if (loading || !settings) {
    return <div className="flex items-center gap-2 text-slate-400 py-6" data-testid="estimate-settings-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading estimate settings…</div>;
  }

  return (
    <div className="space-y-6" data-testid="estimate-settings">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> Estimate defaults</CardTitle>
          <CardDescription>Company-wide rules applied to every new estimate. Admins only.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label>Validity period (days)</Label>
              <Input
                type="number" min={1} max={365}
                value={settings.validity_days}
                onChange={(e) => setSettings({ ...settings, validity_days: parseInt(e.target.value || '0', 10) })}
                data-testid="estimate-validity-days"
              />
              <p className="text-xs text-slate-500 mt-1">After this many days an issued estimate auto-expires. Default 30.</p>
            </div>
            <div>
              <Label>Estimate prefix</Label>
              <Input
                value={settings.estimate_prefix || 'EST-'}
                onChange={(e) => setSettings({ ...settings, estimate_prefix: e.target.value })}
                data-testid="estimate-prefix"
              />
              <p className="text-xs text-slate-500 mt-1">Shown on the PDF + email. Defaults to <span className="font-mono">EST-</span>.</p>
            </div>
          </div>

          <div>
            <Label>Standard terms &amp; conditions</Label>
            <Textarea
              rows={4}
              placeholder={"Add any boilerplate that should appear on every estimate, one bullet per line.\nExample:\nWork performed during normal business hours.\nChange orders require written approval."}
              value={settings.estimate_terms || ''}
              onChange={(e) => setSettings({ ...settings, estimate_terms: e.target.value })}
              data-testid="estimate-terms"
            />
            <p className="text-xs text-slate-500 mt-1">Printed on every estimate PDF, in addition to the standard &quot;valid until&quot; + deposit clauses.</p>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => save({})} disabled={saving} data-testid="estimate-settings-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save defaults
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Deposit policy</CardTitle>
          <CardDescription>Collect an up-front deposit on accepted estimates. Required-above threshold makes the deposit mandatory for large jobs.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Enable deposits</Label>
              <p className="text-xs text-slate-500">Show a deposit line on PDFs and require it on acceptance (Stripe wiring coming next release).</p>
            </div>
            <Switch
              checked={!!settings.deposit_enabled}
              onCheckedChange={(v) => save({ deposit_enabled: v })}
              data-testid="estimate-deposit-toggle"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label>Deposit percentage</Label>
              <Input
                type="number" min={0} max={100} step={0.5}
                value={settings.deposit_pct}
                disabled={!settings.deposit_enabled}
                onChange={(e) => setSettings({ ...settings, deposit_pct: parseFloat(e.target.value || '0') })}
                data-testid="estimate-deposit-pct"
              />
              <p className="text-xs text-slate-500 mt-1">Applied to the grand total. Default 25%.</p>
            </div>
            <div>
              <Label>Mandatory above</Label>
              <Input
                type="number" min={0} step={50}
                placeholder="Leave blank for always optional"
                value={settings.deposit_required_above ?? ''}
                disabled={!settings.deposit_enabled}
                onChange={(e) => setSettings({ ...settings, deposit_required_above: e.target.value === '' ? null : parseFloat(e.target.value) })}
                data-testid="estimate-deposit-threshold"
              />
              <p className="text-xs text-slate-500 mt-1">Estimate totals at or above this amount force the deposit on.</p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => save({})} disabled={saving || !settings.deposit_enabled} data-testid="estimate-deposit-save">Save deposit settings</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Labour roles &amp; rates</CardTitle>
          <CardDescription>Roles your team can pick when building an estimate. Edit the rate per hour to match your shop.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(settings.labour_roles || []).map((r, idx) => (
              <div key={r.id || idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`labour-role-row-${idx}`}>
                <div className="col-span-7">
                  <Input
                    value={r.name}
                    onChange={(e) => updateRole(idx, { name: e.target.value })}
                    placeholder="Role name"
                    data-testid={`labour-role-name-${idx}`}
                  />
                </div>
                <div className="col-span-4">
                  <Input
                    type="number" min={0} step={0.5}
                    value={r.rate_per_hour}
                    onChange={(e) => updateRole(idx, { rate_per_hour: parseFloat(e.target.value || '0') })}
                    placeholder="$ / hr"
                    data-testid={`labour-role-rate-${idx}`}
                  />
                </div>
                <div className="col-span-1">
                  <Button size="icon" variant="ghost" onClick={() => removeRole(idx)} data-testid={`labour-role-remove-${idx}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-slate-700 mt-4 pt-4 grid grid-cols-12 gap-2 items-end">
            <div className="col-span-7">
              <Label>New role name</Label>
              <Input value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.target.value })} placeholder="e.g. Crew Lead" data-testid="new-role-name" />
            </div>
            <div className="col-span-4">
              <Label>Rate / hr</Label>
              <Input type="number" value={newRole.rate_per_hour} onChange={(e) => setNewRole({ ...newRole, rate_per_hour: e.target.value })} data-testid="new-role-rate" />
            </div>
            <div className="col-span-1">
              <Button size="icon" onClick={addRole} disabled={!newRole.name || saving} data-testid="new-role-add"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <Button onClick={() => save({ labour_roles: settings.labour_roles })} disabled={saving} data-testid="labour-roles-save">Save labour rates</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Vehicle &amp; mileage defaults</CardTitle>
          <CardDescription>Defaults pre-filled when adding a vehicle line to an estimate.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <Label>Distance unit</Label>
              <Select
                value={settings.vehicle_unit || 'km'}
                onValueChange={(v) => setSettings({ ...settings, vehicle_unit: v })}
              >
                <SelectTrigger data-testid="vehicle-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="km">Kilometres (Canada)</SelectItem>
                  <SelectItem value="mi">Miles (US)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rate per {settings.vehicle_unit === 'mi' ? 'mile' : 'km'}</Label>
              <Input
                type="number" min={0} step={0.05}
                value={settings.vehicle_rate_per_unit}
                onChange={(e) => setSettings({ ...settings, vehicle_rate_per_unit: parseFloat(e.target.value || '0') })}
                data-testid="vehicle-rate-per-unit"
              />
            </div>
            <div>
              <Label>One-off vehicle fee</Label>
              <Input
                type="number" min={0} step={5}
                value={settings.vehicle_flat_fee}
                onChange={(e) => setSettings({ ...settings, vehicle_flat_fee: parseFloat(e.target.value || '0') })}
                data-testid="vehicle-flat-fee"
              />
              <p className="text-xs text-slate-500 mt-1">Alternative to per-distance billing — flat truck roll fee.</p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => save({})} disabled={saving} data-testid="vehicle-settings-save">Save vehicle defaults</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
